import { useState, useEffect } from "react";
import { 
  Calendar, 
  Plus, 
  Instagram,
  Youtube,
  Music,
  Trash2,
  Edit,
  Link,
  ExternalLink
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const platforms = [
  { value: "instagram", label: "Instagram", icon: Instagram, color: "#E4405F" },
  { value: "tiktok", label: "TikTok", icon: Music, color: "#000000" },
  { value: "youtube", label: "YouTube", icon: Youtube, color: "#FF0000" }
];

const videoStatuses = [
  { value: "none", label: "No Video" },
  { value: "planned", label: "Planned" },
  { value: "filming", label: "Filming" },
  { value: "editing", label: "Editing" },
  { value: "released", label: "Released" }
];

const Content = ({ user }) => {
  const [content, setContent] = useState([]);
  const [releases, setReleases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showContentDialog, setShowContentDialog] = useState(false);
  const [showReleaseDialog, setShowReleaseDialog] = useState(false);

  const [contentForm, setContentForm] = useState({
    title: "",
    platform: "instagram",
    scheduled_date: new Date().toISOString().split('T')[0],
    caption: "",
    hashtags: [],
    link: "",
    status: "scheduled"
  });

  const [releaseForm, setReleaseForm] = useState({
    song_name: "",
    artist: "Coí LeVíañcé",
    release_date: "",
    links: { spotify: "", apple_music: "", youtube: "" },
    promo_plan: "",
    video_status: "none",
    rollout_checklist: []
  });

  const [newHashtag, setNewHashtag] = useState("");
  const [newChecklistItem, setNewChecklistItem] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [contentRes, releasesRes] = await Promise.all([
        fetch(`${API}/content`, { credentials: "include" }),
        fetch(`${API}/releases`, { credentials: "include" })
      ]);

      if (contentRes.ok) {
        const data = await contentRes.json();
        setContent(data?.content ?? data ?? []);
      } else {
        setContent([]);
      }
      if (releasesRes.ok) {
        const data = await releasesRes.json();
        setReleases(data?.releases ?? data ?? []);
      } else {
        setReleases([]);
      }
    } catch (error) {
      toast.error("Failed to load data");
      setContent([]);
      setReleases([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddContent = async () => {
    try {
      const response = await fetch(`${API}/content`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(contentForm)
      });

      if (response.ok) {
        toast.success("Content scheduled");
        setShowContentDialog(false);
        setContentForm({
          title: "",
          platform: "instagram",
          scheduled_date: new Date().toISOString().split('T')[0],
          caption: "",
          hashtags: [],
          link: "",
          status: "scheduled"
        });
        fetchData();
      }
    } catch (error) {
      toast.error("Failed to add content");
    }
  };

  const handleAddRelease = async () => {
    try {
      const response = await fetch(`${API}/releases`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(releaseForm)
      });

      if (response.ok) {
        toast.success("Release added");
        setShowReleaseDialog(false);
        setReleaseForm({
          song_name: "",
          artist: "Coí LeVíañcé",
          release_date: "",
          links: { spotify: "", apple_music: "", youtube: "" },
          promo_plan: "",
          video_status: "none",
          rollout_checklist: []
        });
        fetchData();
      }
    } catch (error) {
      toast.error("Failed to add release");
    }
  };

  const handleDeleteContent = async (id) => {
    if (!window.confirm("Delete this content?")) return;
    try {
      await fetch(`${API}/content/${id}`, { method: "DELETE", credentials: "include" });
      toast.success("Content deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const handleDeleteRelease = async (id) => {
    if (!window.confirm("Delete this release?")) return;
    try {
      await fetch(`${API}/releases/${id}`, { method: "DELETE", credentials: "include" });
      toast.success("Release deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const addHashtag = () => {
    if (!newHashtag.trim()) return;
    const tag = newHashtag.startsWith('#') ? newHashtag.slice(1) : newHashtag;
    setContentForm({ ...contentForm, hashtags: [...contentForm.hashtags, tag] });
    setNewHashtag("");
  };

  const removeHashtag = (index) => {
    const newTags = (contentForm.hashtags || []).filter((_, i) => i !== index);
    setContentForm({ ...contentForm, hashtags: newTags });
  };

  const addChecklistItem = () => {
    if (!newChecklistItem.trim()) return;
    setReleaseForm({
      ...releaseForm,
      rollout_checklist: [...releaseForm.rollout_checklist, { task: newChecklistItem, done: false }]
    });
    setNewChecklistItem("");
  };

  const getPlatformIcon = (platform) => {
    const p = platforms.find(pl => pl.value === platform);
    const Icon = p?.icon || Calendar;
    return <Icon size={18} style={{ color: p?.color }} />;
  };

  const getCalendarDays = () => {
    const today = new Date();
    const days = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      days.push(date);
    }
    return days;
  };

  const getContentForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return (content || []).filter(c => c.scheduled_date === dateStr);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="content-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#FAFAFA]">Content & Marketing</h1>
          <p className="text-[#A1A1AA] mt-1">Manage your content calendar and music releases</p>
        </div>
      </div>

      <Tabs defaultValue="calendar" className="space-y-4">
        <TabsList className="bg-[#121214] border border-[#27272A]">
          <TabsTrigger value="calendar" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0B]">
            Content Calendar
          </TabsTrigger>
          <TabsTrigger value="releases" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0B]">
            Coí LeVíañcé Releases
          </TabsTrigger>
        </TabsList>

        {/* Content Calendar Tab */}
        <TabsContent value="calendar">
          <div className="flex justify-end mb-4">
            <Dialog open={showContentDialog} onOpenChange={setShowContentDialog}>
              <DialogTrigger asChild>
                <Button className="btn-gold" data-testid="add-content-btn">
                  <Plus size={18} className="mr-2" /> Schedule Content
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#121214] border-[#27272A]">
                <DialogHeader>
                  <DialogTitle className="text-[#FAFAFA]">Schedule Content</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <Label className="text-[#A1A1AA]">Title</Label>
                    <Input
                      value={contentForm.title}
                      onChange={(e) => setContentForm({ ...contentForm, title: e.target.value })}
                      placeholder="Post title"
                      className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-[#A1A1AA]">Platform</Label>
                      <Select value={contentForm.platform} onValueChange={(v) => setContentForm({ ...contentForm, platform: v })}>
                        <SelectTrigger className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#1A1A1D] border-[#27272A]">
                          {platforms.map((p) => (
                            <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[#A1A1AA]">Date</Label>
                      <Input
                        type="date"
                        value={contentForm.scheduled_date}
                        onChange={(e) => setContentForm({ ...contentForm, scheduled_date: e.target.value })}
                        className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-[#A1A1AA]">Caption</Label>
                    <Textarea
                      value={contentForm.caption}
                      onChange={(e) => setContentForm({ ...contentForm, caption: e.target.value })}
                      placeholder="Write your caption..."
                      className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                    />
                  </div>
                  <div>
                    <Label className="text-[#A1A1AA]">Hashtags</Label>
                    <div className="flex gap-2">
                      <Input
                        value={newHashtag}
                        onChange={(e) => setNewHashtag(e.target.value)}
                        placeholder="#hashtag"
                        className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                        onKeyPress={(e) => e.key === 'Enter' && addHashtag()}
                      />
                      <Button onClick={addHashtag} variant="outline" className="border-[#27272A]">
                        <Plus size={16} />
                      </Button>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {contentForm.hashtags.map((tag, idx) => (
                        <span 
                          key={idx}
                          className="badge badge-gold cursor-pointer hover:bg-[#D4AF37]/30"
                          onClick={() => removeHashtag(idx)}
                        >
                          #{tag} ×
                        </span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label className="text-[#A1A1AA]">Link (Optional)</Label>
                    <Input
                      value={contentForm.link}
                      onChange={(e) => setContentForm({ ...contentForm, link: e.target.value })}
                      placeholder="https://..."
                      className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                    />
                  </div>
                  <Button onClick={handleAddContent} className="w-full btn-gold">
                    Schedule Post
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {/* Calendar View */}
          <div className="grid grid-cols-7 gap-2">
            {getCalendarDays().map((day, idx) => (
              <div key={idx} className="calendar-cell">
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-sm font-medium ${idx === 0 ? 'text-[#D4AF37]' : 'text-[#FAFAFA]'}`}>
                    {day.toLocaleDateString('en-US', { weekday: 'short' })}
                  </span>
                  <span className="text-xs text-[#71717A]">{day.getDate()}</span>
                </div>
                <div className="space-y-1">
                  {getContentForDate(day).map((c) => (
                    <div 
                      key={c.id}
                      className="p-2 bg-[#1A1A1D] rounded-lg group relative"
                    >
                      <div className="flex items-center gap-1">
                        {getPlatformIcon(c.platform)}
                        <span className="text-xs text-[#FAFAFA] truncate">{c.title}</span>
                      </div>
                      <button 
                        onClick={() => handleDeleteContent(c.id)}
                        className="absolute -top-1 -right-1 opacity-0 group-hover:opacity-100 bg-[#EF4444] text-white rounded-full p-0.5 transition-opacity"
                      >
                        <Trash2 size={10} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Content List */}
          <Card className="bg-[#121214] border-[#27272A] mt-6">
            <CardHeader>
              <CardTitle className="text-[#FAFAFA]">Scheduled Content</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {content.map((c) => (
                  <div 
                    key={c.id}
                    className="flex items-center justify-between p-4 bg-[#1A1A1D] rounded-lg"
                  >
                    <div className="flex items-center gap-4">
                      {getPlatformIcon(c.platform)}
                      <div>
                        <h4 className="font-medium text-[#FAFAFA]">{c.title}</h4>
                        <p className="text-xs text-[#71717A]">{c.scheduled_date}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`badge ${c.status === 'posted' ? 'badge-success' : 'badge-warning'}`}>
                        {c.status}
                      </span>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => handleDeleteContent(c.id)}
                        className="text-[#EF4444]"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                ))}
                {content.length === 0 && (
                  <p className="text-center text-[#71717A] py-8">No content scheduled</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Releases Tab */}
        <TabsContent value="releases">
          <div className="flex justify-end mb-4">
            <Dialog open={showReleaseDialog} onOpenChange={setShowReleaseDialog}>
              <DialogTrigger asChild>
                <Button className="btn-gold" data-testid="add-release-btn">
                  <Plus size={18} className="mr-2" /> Add Release
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#121214] border-[#27272A]">
                <DialogHeader>
                  <DialogTitle className="text-[#FAFAFA]">Add Music Release</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4 max-h-[60vh] overflow-y-auto">
                  <div>
                    <Label className="text-[#A1A1AA]">Song Name</Label>
                    <Input
                      value={releaseForm.song_name}
                      onChange={(e) => setReleaseForm({ ...releaseForm, song_name: e.target.value })}
                      placeholder="Track title"
                      className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-[#A1A1AA]">Artist</Label>
                      <Input
                        value={releaseForm.artist}
                        onChange={(e) => setReleaseForm({ ...releaseForm, artist: e.target.value })}
                        className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                      />
                    </div>
                    <div>
                      <Label className="text-[#A1A1AA]">Release Date</Label>
                      <Input
                        type="date"
                        value={releaseForm.release_date}
                        onChange={(e) => setReleaseForm({ ...releaseForm, release_date: e.target.value })}
                        className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-[#A1A1AA]">Video Status</Label>
                    <Select value={releaseForm.video_status} onValueChange={(v) => setReleaseForm({ ...releaseForm, video_status: v })}>
                      <SelectTrigger className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1A1A1D] border-[#27272A]">
                        {videoStatuses.map((s) => (
                          <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[#A1A1AA]">Promo Plan</Label>
                    <Textarea
                      value={releaseForm.promo_plan}
                      onChange={(e) => setReleaseForm({ ...releaseForm, promo_plan: e.target.value })}
                      placeholder="Marketing strategy..."
                      className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                    />
                  </div>
                  <div>
                    <Label className="text-[#A1A1AA]">Rollout Checklist</Label>
                    <div className="flex gap-2">
                      <Input
                        value={newChecklistItem}
                        onChange={(e) => setNewChecklistItem(e.target.value)}
                        placeholder="Add task"
                        className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                        onKeyPress={(e) => e.key === 'Enter' && addChecklistItem()}
                      />
                      <Button onClick={addChecklistItem} variant="outline" className="border-[#27272A]">
                        <Plus size={16} />
                      </Button>
                    </div>
                    <div className="mt-2 space-y-1">
                      {releaseForm.rollout_checklist.map((item, idx) => (
                        <div key={idx} className="text-sm text-[#A1A1AA] flex items-center gap-2">
                          <span>•</span> {item.task}
                        </div>
                      ))}
                    </div>
                  </div>
                  <Button onClick={handleAddRelease} className="w-full btn-gold">
                    Add Release
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {/* Releases Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {releases.map((release) => (
              <Card key={release.id} className="bg-[#121214] border-[#27272A]" data-testid={`release-card-${release.id}`}>
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-[#D4AF37]">{release.song_name}</h3>
                      <p className="text-sm text-[#A1A1AA]">{release.artist}</p>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleDeleteRelease(release.id)}
                      className="text-[#EF4444]"
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                  
                  <div className="flex items-center gap-4 text-sm text-[#71717A] mb-4">
                    <span className="flex items-center gap-1">
                      <Calendar size={14} /> {release.release_date}
                    </span>
                    <span className={`badge ${
                      release.video_status === 'released' ? 'badge-success' : 
                      release.video_status === 'none' ? 'bg-[#71717A]/20 text-[#71717A]' : 'badge-warning'
                    }`}>
                      Video: {videoStatuses.find(s => s.value === release.video_status)?.label}
                    </span>
                  </div>

                  {release.promo_plan && (
                    <p className="text-sm text-[#A1A1AA] mb-4">{release.promo_plan}</p>
                  )}

                  {release.rollout_checklist?.length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs text-[#71717A] mb-2">Rollout Checklist</p>
                      <div className="space-y-1">
                        {release.rollout_checklist.map((item, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-sm">
                            {item.done ? (
                              <span className="text-[#22C55E]">✓</span>
                            ) : (
                              <span className="text-[#71717A]">○</span>
                            )}
                            <span className={item.done ? "text-[#71717A] line-through" : "text-[#FAFAFA]"}>
                              {item.task}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {(release.links?.spotify || release.links?.apple_music || release.links?.youtube) && (
                    <div className="flex gap-2 pt-4 border-t border-[#27272A]">
                      {release.links.spotify && (
                        <a href={release.links.spotify} target="_blank" rel="noopener noreferrer" 
                           className="text-[#1DB954] hover:opacity-80">
                          <Music size={20} />
                        </a>
                      )}
                      {release.links.apple_music && (
                        <a href={release.links.apple_music} target="_blank" rel="noopener noreferrer"
                           className="text-[#FA233B] hover:opacity-80">
                          <Music size={20} />
                        </a>
                      )}
                      {release.links.youtube && (
                        <a href={release.links.youtube} target="_blank" rel="noopener noreferrer"
                           className="text-[#FF0000] hover:opacity-80">
                          <Youtube size={20} />
                        </a>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
            
            {releases.length === 0 && (
              <div className="col-span-full text-center py-12">
                <Music size={48} className="text-[#71717A] mx-auto mb-3" />
                <p className="text-[#A1A1AA]">No releases yet</p>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Content;
